import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import { Alert, Box, Button, Drawer, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseButton from 'components/@home/drawers/CloseButton';
import FormikTableField from 'components/formik/TableField';
import copy from 'copy-to-clipboard';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useSelector } from 'react-redux';
import authCompany from 'store/selectors/authCompany';
import { useLazyListTeamsQuery } from 'store/app/entities/TeamsSlice';
import getSharedFileLink from 'utils/getSharedFileLink';
import { I18n } from 'utils/i18n';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';

export const ShareDrawerContext = React.createContext({});

/** Opacity crossfade for copy ↔ check icons (same timing as Promoter Ninja LinkPage). */
const COPY_LINK_ICON_TRANSITION_MS = 200;
const copyLinkIconClassNames = {
  enter: 'shareDrawerCopyIcon-enter',
  enterActive: 'shareDrawerCopyIcon-enter-active',
  exit: 'shareDrawerCopyIcon-exit',
  exitActive: 'shareDrawerCopyIcon-exit-active',
};

const CopyLinkIconSlot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  [`&.${copyLinkIconClassNames.enter}`]: { opacity: 0 },
  [`&.${copyLinkIconClassNames.enterActive}`]: {
    opacity: 1,
    transition: `opacity ${COPY_LINK_ICON_TRANSITION_MS}ms ease-in`,
  },
  [`&.${copyLinkIconClassNames.exit}`]: { opacity: 1 },
  [`&.${copyLinkIconClassNames.exitActive}`]: {
    opacity: 0,
    transition: `opacity ${COPY_LINK_ICON_TRANSITION_MS}ms ease-in`,
  },
});

const ShareDrawer = () => {
  const { isOpen, file: folder, onSubmit, close } = useContext(ShareDrawerContext);
  const isTemplate = folder?.kind === 'DocumentFormat';
  const company = useSelector(authCompany);
  const [linkCopyAck, setLinkCopyAck] = useState(false);
  const copyAckTimerRef = useRef(null);
  const copyIconNodeRef = useRef(null);
  const [triggerListTeamsQuery] = useLazyListTeamsQuery();

  const clearCopyAckTimer = useCallback(() => {
    if (copyAckTimerRef.current) {
      clearTimeout(copyAckTimerRef.current);
      copyAckTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      clearCopyAckTimer();
      setLinkCopyAck(false);
    }
  }, [isOpen, clearCopyAckTimer]);

  useEffect(() => () => clearCopyAckTimer(), [clearCopyAckTimer]);

  const shareableLink = useMemo(() => {
    if (!folder || folder.deleted) return null;
    if (folder.kind !== 'Folder' && folder.kind !== 'Document') return null;
    return getSharedFileLink(company, folder);
  }, [company, folder]);

  const handleCopyShareLink = () => {
    if (!shareableLink) return;
    copy(shareableLink);
    clearCopyAckTimer();
    setLinkCopyAck(true);
    copyAckTimerRef.current = setTimeout(() => {
      setLinkCopyAck(false);
      copyAckTimerRef.current = null;
    }, 2000);
  };

  const handleClose = () => {
    close();
  };
  const handleSubmit = ({ permissions: permissionsIn, permissionsRemoved } = {}) => {
    if (!permissionsIn) {
      return;
    }
    const permissions = permissionsIn.map(p => ({ team: p.value._id, role: p.value.role }));
    const teamsRemoved = permissionsRemoved;
    onSubmit({ teamsRemoved, permissions });
    handleClose();
  };

  const teamsColumns = [
    {
      key: 'name',
      disablePadding: false,
      label: I18n.t('ShareDrawer.TeamsTable.TeamName'),
      xsVisible: true,
    },
    {
      key: 'role',
      disablePadding: false,
      label: I18n.t('ShareDrawer.TeamsTable.Permissions'),
      xsVisible: true,
      type: 'dropdown',
      options: ['read', 'admin'].map(value => ({
        value,
        label: I18n.t(`ShareDrawer.permissions.${value}`),
      })),
      defaultValue: 'read',
    },
    {
      key: 'delete',
      xsVisible: true,
      type: 'remove',
    },
  ];

  const permissions = (
    folder?.permissions
      ?.filter(p => p)
      .map(({ team: { _id, name }, role }) => ({
        label: name,
        value: { _id, name, role },
      })) || []
  ).filter(p => p.label);

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose} onClick={e => e.stopPropagation()}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          maxWidth: 'calc(100vw - 40px)',
          width: { xs: '100%', md: 1100 },
        }}
      >
        <Box sx={{ paddingTop: 2.5, paddingLeft: 2.5 }}>
          <CloseButton onClick={handleClose} />
        </Box>
        <Box
          sx={{ display: 'flex', flex: 1, minWidth: 500, flexDirection: 'column', padding: 2.5 }}
        >
          <Typography variant="h6" component="h1">
            {I18n.t(`ShareDrawer.Share with teams`)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 2,
              mb: 2,
              maxWidth: 560,
            }}
          >
            {I18n.t(
              isTemplate
                ? 'ShareDrawer.AccessScopeHelpTemplate'
                : 'ShareDrawer.AccessScopeHelpShared',
            )}
          </Typography>
          {permissions.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {I18n.t(
                isTemplate
                  ? 'ShareDrawer.EmptyTeamsNoticeTemplate'
                  : 'ShareDrawer.EmptyTeamsNoticeShared',
              )}
            </Alert>
          )}
          <Formik initialValues={{ permissions, permissionsRemoved: [] }} onSubmit={handleSubmit}>
            <Form style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1 }}>
                <Field
                  name="permissions"
                  component={FormikTableField}
                  buttonText={I18n.t('ShareDrawer.Add team')}
                  columns={teamsColumns}
                  loadOptions={loadOptionsLazyQuery}
                  additional={{ page: 1, trigger: triggerListTeamsQuery }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {shareableLink ? (
                    <Button
                      type="button"
                      variant="outlined"
                      color="secondary"
                      startIcon={
                        <SwitchTransition mode="out-in">
                          <CSSTransition
                            key={linkCopyAck ? 'check' : 'link'}
                            nodeRef={copyIconNodeRef}
                            timeout={COPY_LINK_ICON_TRANSITION_MS}
                            classNames={copyLinkIconClassNames}
                          >
                            <CopyLinkIconSlot ref={copyIconNodeRef}>
                              {linkCopyAck ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : (
                                <LinkIcon color="primary" sx={{ fontSize: 20 }} />
                              )}
                            </CopyLinkIconSlot>
                          </CSSTransition>
                        </SwitchTransition>
                      }
                      onClick={handleCopyShareLink}
                    >
                      {I18n.t('ShareDrawer.Copy link')}
                    </Button>
                  ) : null}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="secondary"
                    sx={{ color: 'primary.ultraDark' }}
                  >
                    {I18n.t('ShareDrawer.Cancel')}
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    {I18n.t('ShareDrawer.Save')}
                  </Button>
                </Box>
              </Box>
            </Form>
          </Formik>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ShareDrawer;
